/**
 * TypeScript view of the `scrabble` format.
 * Source of truth: `shared/schema/scrabble.{definition,submission,solution,progress}.json`.
 * The constants below are the same literals as `backend/apps/puzzletypes/scrabble/board.py`.
 */

export type Localized = Record<string, string | undefined>;
export type Language = "en" | "fr";
export type Direction = "across" | "down";
export type Cell = [number, number];

export const LANGUAGES: Language[] = ["en", "fr"];
export const SIZE = 15;
export const CENTRE: Cell = [7, 7];
export const RACK_MAX = 7;
export const BINGO_BONUS = 50;
export const BLANK = "?";
export const EMPTY = ".";

export interface ScrabbleDefinition {
  puzzleType: "scrabble";
  typeVersion: number;
  language: Language;
  /** Fifteen rows of fifteen: `.` empty, `A-Z` a tile, `a-z` a blank played as that letter. */
  board: string[];
  /** The player's tiles, `?` for a blank. */
  rack: string;
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

/** A tile taken from the rack and set on the board, not yet submitted. */
export interface PlacedTile {
  r: number;
  c: number;
  letter: string;
  blank: boolean;
}

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface ScrabbleProgress {
  version: 1;
  placed: PlacedTile[];
}

/** One play: the main word as it reads on the board, lowercase marking a blank. */
export interface ScrabblePlay {
  row: number;
  col: number;
  direction: Direction;
  word: string;
}

export type ScrabbleSubmission = ScrabblePlay;
/** The author's reference play, in the submission format. */
export type ScrabbleSolution = ScrabblePlay;

/** What a teammate sees of this player: the cell under the cursor (ADR 0028). */
export interface ScrabblePresence {
  cell: Cell | null;
}

/** T = triple word, D = double word, t = triple letter, d = double letter. */
export const PREMIUM_ROWS = [
  "T..d...T...d..T",
  ".D...t...t...D.",
  "..D...d.d...D..",
  "d..D...d...D..d",
  "....D.....D....",
  ".t...t...t...t.",
  "..d...d.d...d..",
  "T..d...D...d..T",
  "..d...d.d...d..",
  ".t...t...t...t.",
  "....D.....D....",
  "d..D...d...D..d",
  "..D...d.d...D..",
  ".D...t...t...D.",
  "T..d...T...d..T",
] as const;

export type Premium = "TW" | "DW" | "TL" | "DL" | "";

function values(spec: Record<number, string>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [value, letters] of Object.entries(spec))
    for (const letter of letters) out[letter] = Number(value);
  return out;
}

export const LETTER_VALUES: Record<Language, Record<string, number>> = {
  en: values({ 1: "AEILNORSTU", 2: "DG", 3: "BCMP", 4: "FHVWY", 5: "K", 8: "JX", 10: "QZ" }),
  fr: values({ 1: "AEILNORSTU", 2: "DGM", 3: "BCP", 4: "FHV", 8: "JQ", 10: "KWXYZ" }),
};

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

export function cellKey([r, c]: Cell): string {
  return `${r},${c}`;
}

export function sameCell(a: Cell | null, b: Cell | null): boolean {
  return a !== null && b !== null && a[0] === b[0] && a[1] === b[1];
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
